import { MD3LightTheme, MD3DarkTheme } from 'react-native-paper';

/**
 * Premium Coffee Theme - Inspired by Apple and Nothing design systems
 *
 * Design Philosophy:
 * - Apple: Refined, minimalist, with subtle depth and premium feel
 * - Nothing: Clean typography, high contrast, intentional whitespace
 * - Coffee: Warm, inviting, sophisticated earth tones
 */

// Light theme - Clean, bright with warm coffee accents
const coffeeColors = {
  // Primary - Refined espresso brown with warmth
  primary: '#6B4423', // Deeper, more sophisticated coffee
  onPrimary: '#FFFFFF',
  primaryContainer: '#f9f1e669', // Cream-like container
  onPrimaryContainer: '#3D2415',

  // Secondary - Warm caramel tones
  secondary: '#7D5A3C',
  onSecondary: '#FFFFFF',
  secondaryContainer: '#F8EDE3',
  onSecondaryContainer: '#2E1E0F',

  // Tertiary - Subtle copper accent
  tertiary: '#9C6644',
  onTertiary: '#FFFFFF',
  tertiaryContainer: '#FFE4D4',
  onTertiaryContainer: '#3A1F0D',

  // Error states
  error: '#DC3545',
  onError: '#FFFFFF',
  errorContainer: '#FFEAEA',
  onErrorContainer: '#8B1A1A',

  // Surfaces - Apple-inspired clean whites
  background: '#FAFAFA', // Slightly warm white
  onBackground: '#1A1A1A',
  surface: '#FFFFFF',
  onSurface: '#1A1A1A',
  surfaceVariant: '#F5F5F5',
  onSurfaceVariant: '#71717A', // Refined gray

  // Outlines - Subtle, refined borders
  outline: '#D4D4D8',
  outlineVariant: '#E4E4E7',

  // System colors
  shadow: '#000000',
  scrim: '#000000',
  inverseSurface: '#27272A',
  inverseOnSurface: '#F4F4F5',
  inversePrimary: '#E8C8A8',

  // Extended palette for components
  text: '#18181B',
  textSecondary: '#71717A',
  success: '#22C55E',
  successLight: '#DCFCE7',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  primaryLight: '#F5E6D3',
  border: '#E4E4E7',

  // Glass effect colors
  glass: 'rgba(255, 255, 255, 0.8)',
  glassBorder: 'rgba(0, 0, 0, 0.06)',

  // Elevation surfaces - Apple-style subtle elevation
  elevation: {
    level0: 'transparent',
    level1: '#FEFEFE',
    level2: '#FAFAFA',
    level3: '#F5F5F5',
    level4: '#F0F0F0',
    level5: '#EBEBEB',
  },
};

// Dark theme - Nothing-inspired deep blacks with warm accents
const coffeeColorsDark = {
  // Primary - Warm cream accent on dark
  primary: '#E8C8A8', // Warm latte color
  onPrimary: '#3D2415',
  primaryContainer: 'rgb(90, 66, 50)',
  onPrimaryContainer: '#F5E6D3',

  // Secondary - Muted caramel
  secondary: '#D4B896',
  onSecondary: '#2E1E0F',
  secondaryContainer: '#5A4232',
  onSecondaryContainer: '#F8EDE3',

  // Tertiary - Soft copper
  tertiary: '#DEB887',
  onTertiary: '#3A1F0D',
  tertiaryContainer: '#7D5A3C',
  onTertiaryContainer: '#FFE4D4',

  // Error states - Softer for dark mode
  error: '#F87171',
  onError: '#7F1D1D',
  errorContainer: '#991B1B',
  onErrorContainer: '#FECACA',

  // Surfaces - Nothing-inspired pure blacks
  background: '#0A0A0A', // Pure near-black
  onBackground: '#FAFAFA',
  surface: '#141414', // Slightly elevated black
  onSurface: '#FAFAFA',
  surfaceVariant: '#262626', // Card backgrounds
  onSurfaceVariant: '#A1A1AA',

  // Outlines - Subtle on dark
  outline: '#3F3F46',
  outlineVariant: '#27272A',

  // System colors
  shadow: '#000000',
  scrim: '#000000',
  inverseSurface: '#FAFAFA',
  inverseOnSurface: '#18181B',
  inversePrimary: '#6B4423',

  // Extended palette for components
  text: '#FAFAFA',
  textSecondary: '#A1A1AA',
  success: '#4ADE80',
  successLight: '#14532D',
  warning: '#FBBF24',
  warningLight: '#78350F',
  info: '#60A5FA',
  infoLight: '#1E3A5F',
  primaryLight: '#3D2415',
  border: '#27272A',

  // Glass effect colors for dark mode
  glass: 'rgba(20, 20, 20, 0.85)',
  glassBorder: 'rgba(255, 255, 255, 0.08)',

  // Elevation surfaces - Subtle dark elevation
  elevation: {
    level0: 'transparent',
    level1: '#1A1A1A',
    level2: '#1F1F1F',
    level3: '#262626',
    level4: '#2D2D2D',
    level5: '#333333',
  },
};

export const lightTheme = {
  ...MD3LightTheme,
  colors: {
    ...MD3LightTheme.colors,
    ...coffeeColors,
  },
  // Additional theme properties for premium feel
  roundness: 8,
};

export const darkTheme = {
  ...MD3DarkTheme,
  colors: {
    ...MD3DarkTheme.colors,
    ...coffeeColorsDark,
  },
  // Additional theme properties for premium feel
  roundness: 8,
};

export const theme = lightTheme;

export { ThemeProvider, useTheme } from './ThemeContext';
